import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiEdit2, FiUpload } from "react-icons/fi";
import fetchData from "../custom_functions/fetchData";
import patchData from "../custom_functions/patchData";
import formatNumberToUs from "../custom_functions/formatNumberToUs";
import AddFleetCar from "../modals/AddFleetCar";
import ImportVehicle from "../modals/ImportVehicle";
import Button from "../reusable_components/Button";
import { pageTitle } from "../utils/style";
import { url } from "../utils/config";

function Vehicles() {
  const [reloadTable, setReloadTable] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [carCatalog, setCarCatalog] = useState([]);
  const [fleetCars, setFleetCars] = useState([]);
  const [isFleetLoading, setIsFleetLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isNextPage, setIsNextPage] = useState(false);
  const [editingCarId, setEditingCarId] = useState(null);
  const [editValues, setEditValues] = useState({ current_odometer: "", status: "active" });
  const [updatingId, setUpdatingId] = useState(null);
  const [otherData, setOtherData] = useState(null);
  const [search, setSearch] = useState("");

  const isFilterActive = Boolean(search);

  useEffect(() => {
    fetchCarCatalog();
  }, [reloadTable]);

  useEffect(() => {
    fetchFleetCars(page);
  }, [page]);

  useEffect(() => {
    if (!reloadTable) return;
    if (page !== 1) {
      setPage(1);
      return;
    }
    fetchFleetCars(1);
  }, [reloadTable, page]);

  // Debounce search input
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      } else {
        fetchFleetCars(1, search);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(debounceTimer);
  }, [search]);

  function fetchCarCatalog() {
    fetchData(
      url + "/master_cars",
      (error) => {
        toast.error(error.response.data.message, { autoClose: 3000 });
      },
      (data) => {
        setCarCatalog(data.data || []);
      },
      false
    );
  }

  function handleSearchChange(value) {
    setSearch(value);
    if (page !== 1) {
      setPage(1);
    }
  }

  function resetFilters() {
    setSearch("");
    if (page !== 1) {
      setPage(1);
    } else {
      fetchFleetCars(1, "");
    }
  }

  function fetchFleetCars(pageNumber = 1, searchOverride = search) {
    setIsFleetLoading(true);
    const params = new URLSearchParams();
    params.append("page", pageNumber);
    if (searchOverride) params.append("search", searchOverride);

    fetchData(
      `${url}/fleet_customer_car?${params.toString()}`,
      (error) => {
        setIsFleetLoading(false);
        toast.error(error?.response?.data?.message || "Unable to load vehicles", { autoClose: 3000 });
      },
      (response) => {

        setFleetCars(response.data || []);
        setIsNextPage(Boolean(response?.isNextPage));
        setEditingCarId(null);
        setEditValues({ current_odometer: "", status: "active" });
        setIsFleetLoading(false);
      },
      false
    );
  }

  function addCar() {
    setOtherData(null);
    setShowModal(true);
  }

  function handleEditClick(car) {
    if (!car || !car.id) return;
    const odometerValue = car?.current_odometer ?? car?.odometer ?? "";
    setEditingCarId(car.id);
    setEditValues({
      current_odometer: odometerValue !== null && odometerValue !== undefined ? String(odometerValue) : "",
      status: car?.status?.toLowerCase() || "active",
    });
  }

  function cancelEditing() {
    setEditingCarId(null);
    setEditValues({ current_odometer: "", status: "active" });
  }

  function handleEditValueChange(event) {
    const { name, value } = event.target;
    if (name === "current_odometer") {
      if (value === "" || /^\d*$/.test(value)) {
        setEditValues((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }
    setEditValues((prev) => ({ ...prev, [name]: value }));
  }

  function saveCarUpdates(carId) {
    if (!carId) return;
    const odometerValue = editValues.current_odometer.trim();
    if (!odometerValue) {
      toast.error("Please provide the latest odometer reading", { autoClose: 3000 });
      return;
    }

    const numericOdometer = Number(odometerValue);
    if (!Number.isFinite(numericOdometer) || numericOdometer < 0) {
      toast.error("Odometer must be a positive number", { autoClose: 3000 });
      return;
    }

    if (!editValues.status) {
      toast.error("Please select a status", { autoClose: 3000 });
      return;
    }

    setUpdatingId(carId);
    patchData(
      `${url}/fleet_customer_car/${carId}`,
      {
        current_odometer: numericOdometer,
        status: editValues.status,
      },
      (error) => {
        setUpdatingId(null);
        toast.error(error?.response?.data?.message || "Unable to update vehicle", { autoClose: 3000 });
      },
      () => {
        setUpdatingId(null);
        toast.success("Vehicle updated", { autoClose: 3000 });
        cancelEditing();
        fetchFleetCars(page);
      },
      false
    );
  }

  function formatStatus(status) {
    if (!status) return "-";
    return status.replace(/[_-]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function formatMileage(value, unit) {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return `${formatNumberToUs(numeric)}${unit ? ` ${unit}` : ""}`;
    }
    return `${value}${unit ? ` ${unit}` : ""}`;
  }

  function deriveTitle(car) {
    if (!car) return "Unnamed Vehicle";
    const parts = [car?.make, car?.model, car?.trim].filter(Boolean);
    const titleText = parts.join(" ");
    return titleText || car?.vin || "Unnamed Vehicle";
  }

  const statusBadgeStyles = useMemo(
    () => ({
      active: "bg-secondary-light border border-secondary text-secondary font-semibold",
      inactive: "bg-neutral-100 border border-neutral-300 text-neutral-700 font-semibold",
    }),
    []
  );

  const statusOptions = ["active", "inactive"];

  const hasFleetCars = Array.isArray(fleetCars) && fleetCars.length > 0;
  const canGoBack = page > 1;

  function goToPreviousPage() {
    if (canGoBack) {
      setPage((prev) => Math.max(prev - 1, 1));
    }
  }

  function goToNextPage() {
    if (isNextPage) {
      setPage((prev) => prev + 1);
    }
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={pageTitle}>Vehicles</h1>
          <p className="text-xs sm:text-sm text-neutral-600 mt-1">Manage your fleet inventory</p>
        </div>
        <div className='flex flex-col sm:flex-row justify-end items-stretch sm:items-end gap-2 sm:gap-3'>
          <Button
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 sm:px-5 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-neutral-700 shadow-base transition-all hover:bg-neutral-50"
            clickHandler={() => setShowImportModal(true)}
            name={
              <span className="flex items-center gap-2">
                <FiUpload size={16} className="sm:w-[18px] sm:h-[18px]" />
                Import Vehicles
              </span>
            }
          />
          <button
            type="button"
            onClick={() => addCar()}
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 sm:px-8 py-2.5 sm:py-3.5 text-sm sm:text-base font-semibold text-white shadow-base transition-all hover:bg-primary-dark"
          >
            + Add Vehicle
          </button>
        </div>
      </div>
      {showImportModal && (
        <ImportVehicle
          setShowImportModal={setShowImportModal}
          setReloadData={setReloadTable}
        />
      )}
      {showModal && (
        <AddFleetCar
          otherData={otherData}
          setOtherData={setOtherData}
          cars={carCatalog}
          setShowModal={setShowModal}
          showModal={showModal}
          setReloadData={setReloadTable}
        />
      )}
      <div className="rounded-xl border border-neutral-200 bg-white p-3 sm:p-6 shadow-base">
        <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search by make, model, year, or VIN..."
                className="w-full rounded-lg border border-neutral-300 bg-white pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-xs sm:text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary transition-all"
              />
              <svg
                className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <button
            type="button"
            onClick={resetFilters}
            disabled={!isFilterActive}
            className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold transition-all ${isFilterActive
              ? "border-primary bg-primary-light text-primary hover:bg-primary hover:text-white"
              : "border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed"
              }`}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:gap-4">
        {isFleetLoading && (
          <div className="rounded-2xl border border-emerald-100 bg-white px-6 py-12 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]">
            Loading vehicles...
          </div>
        )}

        {!isFleetLoading && !hasFleetCars && (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-4 sm:px-6 py-12 sm:py-16 text-center text-emerald-500 shadow-[0_12px_30px_rgba(22,101,52,0.12)] text-sm">
            No vehicles found. Try importing or adding a new vehicle.
          </div>
        )}

        {!isFleetLoading && hasFleetCars &&
          fleetCars.map((car) => {
            const carId = car?.id || car?.car_id || car?.vin;
            const statusKey = (car?.status || "").toString().replace(/\s+/g, "_").toLowerCase();
            const odometerUnit = car?.odo_unit || car?.odometer_unit || "mi";
            const isEditing = editingCarId === car?.id;
            const isSaving = updatingId === car?.id;
            const canMutate = Boolean(car?.id);
            const cardInitial = (car?.make || car?.model || "?").charAt(0).toUpperCase();

            return (
              <div
                key={carId}
                className="w-full rounded-xl sm:rounded-2xl border border-emerald-100 bg-white px-3 sm:px-5 py-3 sm:py-5 shadow-[0_18px_40px_rgba(22,101,52,0.12)]"
              >
                <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-2.5 sm:gap-4">
                    <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-emerald-100 text-base sm:text-lg font-bold text-emerald-700 flex-shrink-0">
                      {cardInitial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="text-base sm:text-lg font-semibold text-emerald-900 break-words">{deriveTitle(car)}</div>
                        {car?.year && (
                          <span className="rounded-full bg-emerald-50 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-emerald-700 border border-emerald-100 whitespace-nowrap">
                            {car.year}
                          </span>
                        )}
                        {car?.trim && (
                          <span className="rounded-full bg-white px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-emerald-500 border border-emerald-100 whitespace-nowrap">
                            {car.trim}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 sm:mt-3 grid gap-2 sm:gap-3 text-xs sm:text-sm text-emerald-700 grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                        <div>
                          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">VIN</div>
                          <div className="mt-1 font-medium text-emerald-900 break-all">{car?.vin || "-"}</div>
                        </div>
                        <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">License Plate</div>
                            <div className="mt-1 font-medium text-emerald-900 break-all">{car?.plate || "-"}</div>
                          </div>
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500">Current Odometer</div>
                            <div className="mt-1 font-medium text-emerald-900">
                              {formatMileage(car?.current_odometer ?? car?.odometer, odometerUnit)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between gap-2 sm:flex-col sm:items-end sm:justify-start sm:gap-3 self-stretch sm:self-start lg:self-center">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-500 whitespace-nowrap">Status:</span>
                      <span
                        className={`rounded-full px-2.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-semibold ${statusBadgeStyles[statusKey] || "bg-slate-100 border border-slate-200 text-slate-600"
                          }`}
                      >
                        {formatStatus(car?.status)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => (isEditing ? cancelEditing() : handleEditClick(car))}
                      disabled={isSaving || !canMutate}
                      className={`inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-emerald-200 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${isEditing
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-white text-emerald-700 hover:bg-emerald-50"
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      <FiEdit2 size={14} className="sm:w-4 sm:h-4" />
                      <span>{isEditing ? "Close" : "Edit"}</span>
                    </button>
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-3 sm:mt-5 rounded-xl sm:rounded-2xl border border-emerald-100 bg-emerald-50/40 p-3 sm:p-4">
                    <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-600">
                          Current Odometer
                        </label>
                        <input
                          type="text"
                          name="current_odometer"
                          inputMode="numeric"
                          value={editValues.current_odometer}
                          onChange={handleEditValueChange}
                          className="w-full rounded-lg border border-emerald-200 bg-white px-2.5 sm:px-3 py-2 text-xs sm:text-sm text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                          placeholder="Enter odometer"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-emerald-600">
                          Status
                        </label>
                        <select
                          name="status"
                          value={editValues.status}
                          onChange={handleEditValueChange}
                          className="w-full rounded-lg border border-emerald-200 bg-white px-2.5 sm:px-3 py-2 text-xs sm:text-sm text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        >
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {formatStatus(status)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={() => saveCarUpdates(car?.id)}
                        disabled={isSaving}
                        className={`w-full sm:w-auto rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white ${isSaving ? "bg-emerald-300" : "bg-emerald-600 hover:bg-emerald-700"
                          }`}
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        disabled={isSaving}
                        className="w-full sm:w-auto rounded-full border border-emerald-200 px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {(!isFleetLoading && hasFleetCars) && (
        <div className="mt-6 sm:mt-8 flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs sm:text-sm font-medium text-emerald-600 text-center sm:text-left">Page {page}</div>
          <div className="flex gap-2 sm:gap-3">
            <button
              type="button"
              onClick={goToPreviousPage}
              disabled={!canGoBack || isFleetLoading}
              className={`flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full border text-xs sm:text-sm font-semibold transition-colors ${canGoBack && !isFleetLoading
                ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={goToNextPage}
              disabled={!isNextPage || isFleetLoading}
              className={`flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full border text-xs sm:text-sm font-semibold transition-colors ${isNextPage && !isFleetLoading
                ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)] hover:bg-emerald-700"
                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vehicles;
